import { openDB } from 'idb';
import { Settings } from '../Models/UATConstants';
import { InsightsClient } from 'src/utils/InsightsClient';
import { TraceMessages } from '../Models/TelemetryConstants';

// Helper function to truncate the database name if it's too long
const truncateName = (name: string, maxLength: number): string => {
  return name.length > maxLength ? name.substring(0, maxLength) : name;
};

const WebUIUrl: string = import.meta.env?.VITE_WEBUIURL || 'default';
const MAX_DB_NAME_LENGTH = 100; // Set a safe limit for the database name
const DB_NAME = truncateName(WebUIUrl, MAX_DB_NAME_LENGTH);
const STORE_NAME = 'ApiCache';

export const initDB = async () => {
  return openDB(DB_NAME, 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    },
  });
};

export const setCache = async (key: string, data: any) => {
  const db = await initDB();
  // Serialize the data before storing it
  const serializedData = JSON.stringify(data);

  const cacheTimestampUTC = new Date().toISOString();

  await db.put(STORE_NAME, {
    key,
    data: serializedData,
    timestampMs: Date.now(),
    timestampUTC: cacheTimestampUTC,
  });
  InsightsClient.trackTrace({
    message: TraceMessages.indexedDBCacheUpdate(key),
  });
};

export const getCache = async (key: string) => {
  const db = await initDB();
  const cached = await db.get(STORE_NAME, key);
  if (cached) {
    try {
      const parsedData = JSON.parse(cached.data);
      return {
        ...cached,
        data: parsedData,
      };
    } catch (error) {
      console.error(`Error deserializing cached data for key: ${key}`, error);
      await clearCache(key); // Clear corrupted cache
    }
  }
  return null;
};

export const clearCache = async (key: string) => {
  const db = await initDB();
  await db.delete(STORE_NAME, key);
};

/**
 * Fetches data with caching support.
 * If valid cached data exists, it is returned immediately.
 * The cache is updated in the background if valid cached data is returned.
 * If the cache is expired or does not exist, the data is fetched using the provided fetch function
 *
 * @param key - The unique key used to identify the cached data.
 * @param fetchFn - A function that fetches the data when the cache is expired or missing.
 * @param cacheDuration - The duration (in milliseconds) for which the cache is considered valid. Defaults to 1 hour (3600000 ms).
 * @returns The cached data if cache is valid, or the fetched data if the cache is expired or missing.
 */
export const fetchWithIndexedDB = async (
  key: string,
  fetchFn: () => Promise<any>,
  cacheDuration = Settings.IDB_CACHE_DURATION,
  transformFn?: (data: any) => any // Optional transform function
) => {
  const cachedData = await getCache(key);

  if (cachedData) {
    const { data, timestampMs } = cachedData;
    const isExpired = Date.now() - timestampMs > cacheDuration;

    if (!isExpired && cachedData.data) {
      InsightsClient.trackTrace({
        message: TraceMessages.indexedDBCacheHit(key),
      });
      fetchAndUpdateCache(key, fetchFn, transformFn); // Trigger background fetch
      return cachedData.data; // Return cached data
    }
  }

  InsightsClient.trackTrace({
    message: TraceMessages.indexedDBCacheMiss(key),
  });

  const rawData = await fetchFn();
  const data = transformFn ? transformFn(rawData) : rawData; // Apply transform if provided
  await setCache(key, data);

  return data;
};

// Updated fetchAndUpdateCache to support transformFn
const fetchAndUpdateCache = async (
  key: string,
  fetchFn: () => Promise<any>,
  transformFn?: (data: any) => any
) => {
  try {
    const rawData = await fetchFn();
    const newData = transformFn ? transformFn(rawData) : rawData; // Apply transform if provided
    await setCache(key, newData);
  } catch (error) {
    console.error('Error fetching and updating cache:', error);
  }
};
